import { memo } from 'react';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { useGlobalAudioClock, useGlobalAudioPlayback } from '@/audio/GlobalAudioContext';
import { useGlobalAudioScrub } from '@/audio/useGlobalAudioScrub';
import { formatAudioClock } from './projectFilesPopoverUtils';
import './ProjectFileAudioPlayer.css';

export type ProjectFileAudioPlayerProps = {
  instanceId: string;
  src: string;
  labelForA11y: string;
  disabled?: boolean;
};

function LiveCurrentTime() {
  const { currentTimeSec } = useGlobalAudioClock();
  return <>{formatAudioClock(currentTimeSec)}</>;
}

function IdleTrack({ labelForA11y }: { labelForA11y: string }) {
  return (
    <>
      <div className="project-file-audio-player__track-wrap">
        <div
          className="project-file-audio-player__track"
          role="slider"
          tabIndex={-1}
          aria-label={`Позиция воспроизведения: ${labelForA11y}`}
          aria-valuemin={0}
          aria-valuemax={1000}
          aria-valuenow={0}
          aria-valuetext={`${formatAudioClock(0)} из ${formatAudioClock(0)}`}
          aria-disabled
        >
          <div className="project-file-audio-player__track-inner">
            <div className="project-file-audio-player__fill" style={{ width: '0%' }} />
            <div className="project-file-audio-player__thumb" style={{ left: '0%' }} aria-hidden />
          </div>
        </div>
      </div>
      <span
        className="project-file-audio-player__time project-file-audio-player__time--end"
        aria-hidden="true"
      >
        {formatAudioClock(0)}
      </span>
    </>
  );
}

function LiveTrack({ labelForA11y }: { labelForA11y: string }) {
  const scrub = useGlobalAudioScrub();
  return (
    <>
      <div className="project-file-audio-player__track-wrap">
        <div
          ref={scrub.barRef}
          className={`project-file-audio-player__track${scrub.isScrubbing ? ' project-file-audio-player__track--dragging' : ''}`}
          role="slider"
          tabIndex={0}
          aria-label={`Позиция воспроизведения: ${labelForA11y}`}
          aria-valuemin={0}
          aria-valuemax={scrub.ariaValueMax}
          aria-valuenow={scrub.ariaValueNow}
          aria-valuetext={`${formatAudioClock(scrub.currentTimeSec)} из ${formatAudioClock(scrub.durationSec)}`}
          aria-disabled={!scrub.durationKnown}
          onPointerDown={scrub.onTrackPointerDown}
          onPointerMove={scrub.onTrackPointerMove}
          onPointerUp={scrub.onTrackPointerUp}
          onPointerCancel={scrub.onTrackPointerCancel}
          onKeyDown={scrub.onTrackKeyDown}
        >
          <div className="project-file-audio-player__track-inner">
            <div
              className="project-file-audio-player__fill"
              style={{ width: `${scrub.progressRatio * 100}%` }}
            />
            <div
              className="project-file-audio-player__thumb"
              style={{ left: `${scrub.progressRatio * 100}%` }}
              aria-hidden
            />
          </div>
        </div>
      </div>
      <span
        className="project-file-audio-player__time project-file-audio-player__time--end"
        aria-hidden="true"
      >
        {formatAudioClock(scrub.durationSec)}
      </span>
    </>
  );
}

export const ProjectFileAudioPlayer = memo(function ProjectFileAudioPlayer({
  instanceId,
  src,
  labelForA11y,
  disabled = false,
}: ProjectFileAudioPlayerProps) {
  const { track, isPlaying, play } = useGlobalAudioPlayback();
  const isCurrent = track?.instanceId === instanceId;
  const showPlaying = isCurrent && isPlaying;
  const live = isCurrent && !disabled;

  if (!src.trim()) return null;

  return (
    <div
      className={`project-file-audio-player${disabled ? ' project-file-audio-player--disabled' : ''}`}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="project-file-audio-player__row">
        <div className="project-file-audio-player__lead">
          <IconButton
            type="button"
            size="s"
            variant="default"
            className="project-file-audio-player__play-btn"
            aria-label={
              showPlaying ? `Пауза: ${labelForA11y}` : `Воспроизвести: ${labelForA11y}`
            }
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              if (disabled) return;
              play({
                instanceId,
                src,
                title: labelForA11y,
                source: 'project-file',
              });
            }}
          >
            {showPlaying ? (
              <i className="fa-solid fa-pause fa-fw" aria-hidden />
            ) : (
              <i className="fa-solid fa-play fa-fw" aria-hidden />
            )}
          </IconButton>
          <span
            className="project-file-audio-player__time project-file-audio-player__time--current"
            aria-hidden="true"
          >
            {live ? <LiveCurrentTime /> : formatAudioClock(0)}
          </span>
        </div>
        {live ? <LiveTrack labelForA11y={labelForA11y} /> : <IdleTrack labelForA11y={labelForA11y} />}
      </div>
    </div>
  );
});
